import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import { API_URL } from '../../config/api.ts';
import PlayerName from '../Common/PlayerName.tsx';
import './ValentineHub.css';

const HEART_EMOJIS = ['💕', '💗', '💖', '💘', '💝', '❤️', '🩷', '💜', '🌹', '✨'];
const LOVE_QUOTES = [
  "L'amour, c'est comme le Wi-Fi… tu sens quand tu perds la connexion 📶",
  "Tu es ma bonne réponse dans un monde de QCM 💯",
  "Avec toi, j'ai pas besoin d'indice 💡",
  "T'es le bonus x2 de ma vie ✨",
  "Mon cœur bat plus vite que le timer à 5 secondes ⏱️",
  "Tu es la réponse que je choisis les yeux fermés 😌",
  "Notre love story a plus de chapitres qu'un quiz culture G 📖",
  "T'es pas un piège, t'es la bonne réponse 🎯",
  "Avec toi c'est combo parfait, zéro erreur 🔥",
  "Je suis tombé(e) pour toi plus vite qu'un streak qui s'arrête 💀",
  "Tu es mon 12/12 en compatibilité 💘",
  "Mon cœur fait ding quand tu es là… comme une bonne réponse ✅",
  "T'es le joker que je garde précieusement 🃏",
  "Ensemble on est imbattables, même en mode difficile 💪",
  "Tu me fais le même effet qu'un streak de 50 🤯",
  "L'amour c'est aveugle, mais nous on voit clair ensemble 👀",
  "T'es la seule personne pour qui j'accepte de perdre 🏳️",
  "Notre alchimie > n'importe quel score 🧪",
  "T'es pas une question piège, t'es la réponse évidente 💖",
  "Cupidon a visé juste cette fois 🏹",
  "On est le duo que personne peut battre 👑",
  "Mon cœur bat à la fréquence de tes messages 📱",
  "T'es ma personne préférée, et c'est pas un QCM 💕",
  "Ensemble même les questions impossibles deviennent faciles 🌟",
];

interface PairInfo {
  id: number;
  user1_uid: string;
  user1_name: string;
  user2_uid?: string;
  user2_name?: string;
  invite_code: string;
  status: string;
}

interface LeaderboardCouple {
  pair_id: number;
  user1_name: string;
  user2_name: string;
  combined_score: number;
  score1?: number;
  score2?: number;  days1: number;
  days2: number;}

const ValentineHub: React.FC = () => {
  const navigate = useNavigate();
  const { code: urlJoinCode } = useParams<{ code?: string }>();
  const [token, setToken] = useState<string | null>(null);
  const [userUid, setUserUid] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [pair, setPair] = useState<PairInfo | null>(null);
  const [pendingInvite, setPendingInvite] = useState<PairInfo | null>(null);
  const [joinCode, setJoinCode] = useState(urlJoinCode || '');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [leaderboard, setLeaderboard] = useState<LeaderboardCouple[]>([]);
  const [dailyQuote] = useState(() => LOVE_QUOTES[Math.floor(Math.random() * LOVE_QUOTES.length)]);

  // Auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user: any) => {
      if (user) {
        const t = await user.getIdToken();
        setToken(t);
        setUserUid(user.uid);
      }
    });
    return () => unsub();
  }, []);

  // Load data
  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [pairRes, statusRes] = await Promise.all([
        fetch(`${API_URL}/api/valentine/pair/mine`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/valentine/daily/status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => null)
      ]);
      const pairData = await pairRes.json();
      setPair(pairData.pair || null);
      setPendingInvite(pairData.pending || null);
      if (statusRes && statusRes.ok) {
        const statusData = await statusRes.json();
        setLeaderboard(statusData.couplesTop || []);
      }
    } catch (e) {
      console.error('Valentine load error:', e);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-join if URL contains a code
  useEffect(() => {
    if (urlJoinCode && token && !pair) {
      handleJoinPair();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlJoinCode, token]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // Create invite
  const handleCreateInvite = async () => {
    if (!token) return;
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/valentine/pair/create`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.alreadyPaired) {
        setPair(data.pair);
        showToast('Tu as déjà une âme sœur ! 💕');
      } else if (data.pair) {
        setPendingInvite(data.pair);
        showToast('Invitation créée ! 🎉');
      }
    } catch (e) {
      setError('Erreur lors de la création de l\'invitation');
    }
  };

  // Join via code
  const handleJoinPair = async () => {
    if (!token || !joinCode.trim()) return;
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/valentine/pair/join`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: joinCode.trim().toUpperCase() })
      });
      const data = await res.json();
      if (res.ok && data.pair) {
        setPair(data.pair);
        setPendingInvite(null);
        showToast('Âmes sœurs connectées ! 💕✨');
      } else {
        setError(data.error || 'Code invalide');
      }
    } catch (e) {
      setError('Erreur de connexion');
    }
  };

  // Break pair
  const handleBreakPair = async () => {
    if (!token || !window.confirm('Vraiment briser ce lien ? 💔')) return;
    try {
      await fetch(`${API_URL}/api/valentine/pair/mine`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setPair(null);
      setPendingInvite(null);
      showToast('Cœur brisé... 💔');
      loadData();
    } catch (e) {
      setError('Erreur');
    }
  };

  // Copy invite code
  const handleCopyCode = (code: string) => {
    const url = `${window.location.origin}/valentine/join/${code}`;
    navigator.clipboard.writeText(url).then(() => {
      showToast('Lien copié ! Envoie-le à ton crush 💌');
    });
  };

  // Get partner name
  const getPartnerName = () => {
    if (!pair) return '';
    if (pair.user1_uid === userUid) return pair.user2_name || '???';
    return pair.user1_name || '???';
  };

  const getMyNameInPair = () => {
    if (!pair) return '';
    if (pair.user1_uid === userUid) return pair.user1_name;
    return pair.user2_name || '';
  };

  const getPartnerUid = () => {
    if (!pair) return '';
    if (pair.user1_uid === userUid) return pair.user2_uid || '';
    return pair.user1_uid;
  };

  const getRankEmoji = (rank: number) => {
    if (rank === 1) return '👑';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}.`;
  };

  const getRankClass = (rank: number) => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'silver';
    if (rank === 3) return 'bronze';
    return 'normal';
  };

  if (loading) {
    return (
      <div className="valentine-hub">
        <div className="valentine-hearts-rain">
          {HEART_EMOJIS.map((h, i) => <span key={i} className="valentine-heart-particle">{h}</span>)}
        </div>
        <div className="valentine-loading">
          <div className="valentine-loading-hearts">
            <span>💕</span><span>💗</span><span>💖</span>
          </div>
          <p>Cupidon prépare ses flèches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="valentine-hub">
      {/* Heart rain */}
      <div className="valentine-hearts-rain">
        {HEART_EMOJIS.concat(['💕', '💗', '💖', '❤️', '🩷']).map((h, i) => (
          <span key={i} className="valentine-heart-particle">{h}</span>
        ))}
      </div>

      {/* Cupid arrow */}
      <div className="valentine-cupid-arrow">🏹💘</div>

      {/* Back button */}
      <button className="valentine-back-btn" onClick={() => navigate('/')}>
        ← Retour
      </button>

      {/* Title */}
      <div className="valentine-title">
        <span className="valentine-title-emoji">💕</span>
        <h1>Mode Saint-Valentin</h1>
        <p className="valentine-subtitle">L'amour est dans le quiz !</p>
      </div>

      {/* Love quote */}
      <div className="valentine-love-quote">
        « {dailyQuote} »
      </div>

      {/* Partner status */}
      {pair && pair.status === 'active' ? (
        <div className="valentine-partner-status">
          <div className="valentine-partner-card">
            <div className="valentine-partner-couple">
              <div className="valentine-partner-name top">
                <PlayerName userName={getMyNameInPair()} userId={userUid} checkEffect />
              </div>
              <div className="valentine-partner-heart-separator">
                <span className="valentine-partner-heart-line" />
                <span className="valentine-partner-heart-icon">❤️</span>
                <span className="valentine-partner-heart-line" />
              </div>
              <div className="valentine-partner-name bottom">
                <PlayerName userName={getPartnerName()} userId={getPartnerUid()} checkEffect />
              </div>
            </div>
            <div className="valentine-partner-label">Âmes sœurs</div>
          </div>
          <button className="valentine-break-btn" onClick={handleBreakPair}>
            💔 Se séparer
          </button>
        </div>
      ) : (
        <div className="valentine-invite-section">
          {pendingInvite ? (
            <>
              <div className="valentine-invite-code-box">
                <div style={{ color: 'rgba(255,182,193,0.7)', fontSize: '0.85rem', marginBottom: 8 }}>
                  Ton code d'invitation :
                </div>
                <div className="valentine-invite-code">{pendingInvite.invite_code}</div>
                <div className="valentine-invite-share-label">
                  Partage ce code à ton âme sœur 💌
                </div>
                <button
                  className="valentine-invite-btn"
                  style={{ marginTop: 12, fontSize: '0.85rem', padding: '8px 18px' }}
                  onClick={() => handleCopyCode(pendingInvite.invite_code)}
                >
                  📋 Copier le lien
                </button>
              </div>
              <div className="valentine-or-divider">ou</div>
            </>
          ) : (
            <>
              <button className="valentine-invite-btn" onClick={handleCreateInvite}>
                💌 Inviter mon âme sœur
              </button>
              <div className="valentine-or-divider">ou</div>
            </>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="valentine-join-input"
              placeholder="Code..."
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              maxLength={9}
            />
            <button className="valentine-join-btn" onClick={handleJoinPair} disabled={!joinCode.trim()}>
              💕 Rejoindre
            </button>
          </div>
        </div>
      )}

      {error && <div className="valentine-error">💔 {error}</div>}

      {/* Mode cards */}
      <div className="valentine-cards-grid">
        <div className="valentine-mode-card" onClick={() => navigate('/valentine/daily')}>
          <span className="valentine-card-emoji">💕</span>
          <div className="valentine-card-title">Daily en Duo</div>
          <div className="valentine-card-desc">
            Streak quotidien !<br />
            Score combiné avec ton âme sœur
          </div>
          <span className="valentine-card-badge">Streak / jour</span>
        </div>

        <div className="valentine-mode-card" onClick={() => navigate('/valentine/compatibility')}>
          <span className="valentine-card-emoji">💘</span>
          <div className="valentine-card-title">Test de Compatibilité</div>
          <div className="valentine-card-desc">
            12 questions fun & goofy<br />
            Trouve ton âme sœur !
          </div>
          <span className="valentine-card-badge">Partageable</span>
        </div>
      </div>

      {/* Leaderboard */}
      {leaderboard.length > 0 && (
        <div className="valentine-leaderboard-section">
          <div className="valentine-leaderboard-title">🏆 Classement des Couples</div>
          <ul className="valentine-leaderboard-list">
            {leaderboard.slice(0, 5).map((c, i) => (
              <li key={c.pair_id} className="valentine-leaderboard-item">
                <span className={`valentine-leaderboard-rank ${getRankClass(i + 1)}`}>
                  {getRankEmoji(i + 1)}
                </span>
                <span className="valentine-leaderboard-couple">
                  {c.user1_name || '???'} ❤️ {c.user2_name || '???'}
                </span>
                <span className="valentine-leaderboard-score">
                  {c.combined_score} pts
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Toast */}
      {toast && <div className="valentine-toast">{toast}</div>}
    </div>
  );
};

export default ValentineHub;
